import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUIStore } from './ui'
import apiService from '../services/apiService'
import logService from '../services/logService'

export const useProductStore = defineStore('products', () => {
  const uiStore = useUIStore()

  // State
  const products = ref([])
  const topProducts = ref([])
  const currentProduct = ref(null)
  const categories = ref([])
  const loading = ref(false)
  const meta = ref({})

  // ==================== PRODUCTS ====================

  const fetchProducts = async (params = {}) => {
    try {
      loading.value = true
      logService.info('Fetching products...', params)

      const response = await apiService.get('/products', params)

      if (response.success) {
        products.value = response.data
        meta.value = response.meta || {}
        return response
      } else {
        throw new Error(response.message || 'Failed to fetch products')
      }
    } catch (error) {
      logService.error('Fetch Products Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return { data: [], meta: {}, success: false }
    } finally {
      loading.value = false
    }
  }

  const fetchProduct = async (id) => {
    try {
      loading.value = true
      logService.info(`Fetching product: ${id}`)

      const response = await apiService.get(`/products/${id}`)

      if (response.success) {
        currentProduct.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Product not found')
      }
    } catch (error) {
      logService.error(`Fetch Product Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const fetchAdminProducts = async (params = {}) => {
    try {
      loading.value = true
      logService.info('Fetching admin products...', params)

      const response = await apiService.get('/admin/products', params)

      if (response.success) {
        products.value = response.data
        meta.value = response.meta || {}
        return response
      } else {
        throw new Error(response.message || 'Failed to fetch products')
      }
    } catch (error) {
      logService.error('Fetch Admin Products Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return { data: [], meta: {}, success: false }
    } finally {
      loading.value = false
    }
  }

  const addProduct = async (productData) => {
    try {
      loading.value = true
      logService.info('Adding product...', productData)

      const response = await apiService.post('/admin/products', productData)

      if (response.success) {
        products.value.unshift(response.data)
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Product created successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to create product')
      }
    } catch (error) {
      logService.error('Add Product Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    } finally {
      loading.value = false
    }
  }

  const updateProduct = async (id, productData) => {
    try {
      loading.value = true
      logService.info(`Updating product: ${id}`, productData)

      const response = await apiService.put(`/admin/products/${id}`, productData)

      if (response.success) {
        const index = products.value.findIndex(item => item.id === id)
        if (index !== -1) {
          products.value[index] = response.data
        }
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Product updated successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to update product')
      }
    } catch (error) {
      logService.error(`Update Product Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    } finally {
      loading.value = false
    }
  }

  const deleteProduct = async (id) => {
    try {
      loading.value = true
      logService.info(`Deleting product: ${id}`)

      const response = await apiService.delete(`/admin/products/${id}`)

      if (response.success) {
        products.value = products.value.filter(item => item.id !== id)
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Product deleted successfully'
        })
        return true
      } else {
        throw new Error(response.message || 'Failed to delete product')
      }
    } catch (error) {
      logService.error(`Delete Product Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    } finally {
      loading.value = false
    }
  }

  // ==================== TOP PRODUCTS ====================

  const fetchTopProducts = async () => {
    try {
      loading.value = true
      logService.info('Fetching top products...')
      const response = await apiService.get('/products/top')

      if (response.success) {
        topProducts.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch top products')
      }
    } catch (error) {
      logService.error('Fetch Top Products Error:', error)
      return []
    } finally {
      loading.value = false
    }
  }

  const toggleTopProduct = async (id) => {
    try {
      logService.info(`Toggling top status for product: ${id}`)
      const response = await apiService.post(`/admin/products/${id}/toggle-top`)

      if (response.success) {
        // Update local state in both products and topProducts
        const prodIndex = products.value.findIndex(p => p.id === id)
        if (prodIndex !== -1) {
          products.value[prodIndex].is_top = response.data.is_top
        }

        if (response.data.is_top) {
          if (!topProducts.value.find(p => p.id === id)) {
            topProducts.value.push(response.data)
          }
        } else {
          topProducts.value = topProducts.value.filter(p => p.id !== id)
        }

        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Product top status updated'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to toggle top status')
      }
    } catch (error) {
      logService.error(`Toggle Top Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const updateTopOrder = async (orderData) => {
    try {
      logService.info('Updating top products order...', orderData)
      const response = await apiService.post('/admin/products/update-top-order', { products: orderData })

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Top order updated successfully'
        })
        return true
      } else {
        throw new Error(response.message || 'Failed to update order')
      }
    } catch (error) {
      logService.error('Update Top Order Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  // ==================== IMAGE UPLOADS ====================

  const uploadIcon = async (productId, file) => {
    try {
      logService.info(`Uploading icon for product: ${productId}`)
      const formData = new FormData()
      formData.append('image', file)

      const response = await apiService.upload(`/admin/products/${productId}/upload-icon`, formData)

      if (response.success) {
        const index = products.value.findIndex(item => item.id === productId)
        if (index !== -1) {
          products.value[index].icon_image = response.data.icon_image
          products.value[index].icon_image_url = response.data.icon_image_url
        }
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Icon uploaded successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to upload icon')
      }
    } catch (error) {
      logService.error(`Upload Icon Error [${productId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const uploadBanner = async (productId, file) => {
    try {
      logService.info(`Uploading banner for product: ${productId}`)
      const formData = new FormData()
      formData.append('image', file)

      const response = await apiService.upload(`/admin/products/${productId}/upload-banner`, formData)

      if (response.success) {
        const index = products.value.findIndex(item => item.id === productId)
        if (index !== -1) {
          products.value[index].banner_image = response.data.banner_image
          products.value[index].banner_image_url = response.data.banner_image_url
        }
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Banner uploaded successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to upload banner')
      }
    } catch (error) {
      logService.error(`Upload Banner Error [${productId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const uploadScreenshot = async (productId, file, caption = null) => {
    try {
      logService.info(`Uploading screenshot for product: ${productId}`)
      const formData = new FormData()
      formData.append('image', file)
      if (caption) {
        formData.append('caption', caption)
      }

      const response = await apiService.upload(`/admin/products/${productId}/screenshots`, formData)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Screenshot uploaded successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to upload screenshot')
      }
    } catch (error) {
      logService.error(`Upload Screenshot Error [${productId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const deleteScreenshot = async (screenshotId) => {
    try {
      logService.info(`Deleting screenshot: ${screenshotId}`)
      const response = await apiService.delete(`/admin/products/screenshots/${screenshotId}`)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Screenshot deleted successfully'
        })
        return true
      } else {
        throw new Error(response.message || 'Failed to delete screenshot')
      }
    } catch (error) {
      logService.error(`Delete Screenshot Error [${screenshotId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  // ==================== TECHNICAL FEATURES ====================

  const addFeature = async (productId, featureData) => {
    try {
      logService.info(`Adding feature to product: ${productId}`, featureData)
      const response = await apiService.post(`/admin/products/${productId}/features`, featureData)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Feature added successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to add feature')
      }
    } catch (error) {
      logService.error(`Add Feature Error [${productId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const updateFeature = async (featureId, featureData) => {
    try {
      logService.info(`Updating feature: ${featureId}`, featureData)
      const response = await apiService.put(`/admin/products/features/${featureId}`, featureData)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Feature updated successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to update feature')
      }
    } catch (error) {
      logService.error(`Update Feature Error [${featureId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const deleteFeature = async (featureId) => {
    try {
      logService.info(`Deleting feature: ${featureId}`)
      const response = await apiService.delete(`/admin/products/features/${featureId}`)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Feature deleted successfully'
        })
        return true
      } else {
        throw new Error(response.message || 'Failed to delete feature')
      }
    } catch (error) {
      logService.error(`Delete Feature Error [${featureId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  // ==================== TESTIMONIALS ====================

  const addTestimonial = async (productId, testimonialData) => {
    try {
      logService.info(`Adding testimonial to product: ${productId}`, testimonialData)
      const response = await apiService.post(`/admin/products/${productId}/testimonials`, testimonialData)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Testimonial added successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to add testimonial')
      }
    } catch (error) {
      logService.error(`Add Testimonial Error [${productId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const updateTestimonial = async (testimonialId, testimonialData) => {
    try {
      logService.info(`Updating testimonial: ${testimonialId}`, testimonialData)
      const response = await apiService.put(`/admin/products/testimonials/${testimonialId}`, testimonialData)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Testimonial updated successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to update testimonial')
      }
    } catch (error) {
      logService.error(`Update Testimonial Error [${testimonialId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const deleteTestimonial = async (testimonialId) => {
    try {
      logService.info(`Deleting testimonial: ${testimonialId}`)
      const response = await apiService.delete(`/admin/products/testimonials/${testimonialId}`)

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Testimonial deleted successfully'
        })
        return true
      } else {
        throw new Error(response.message || 'Failed to delete testimonial')
      }
    } catch (error) {
      logService.error(`Delete Testimonial Error [${testimonialId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  // ==================== CATEGORIES ====================

  const fetchCategories = async () => {
    try {
      logService.info('Fetching categories...')
      const response = await apiService.get('/product-categories')

      if (response.success) {
        categories.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch categories')
      }
    } catch (error) {
      logService.error('Fetch Categories Error:', error)
      return []
    }
  }

  const fetchAdminCategories = async () => {
    try {
      logService.info('Fetching admin categories...')
      const response = await apiService.get('/admin/product-categories')

      if (response.success) {
        categories.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch categories')
      }
    } catch (error) {
      logService.error('Fetch Admin Categories Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return []
    }
  }

  const addCategory = async (categoryData) => {
    try {
      logService.info('Adding category...', categoryData)
      const response = await apiService.post('/admin/product-categories', categoryData)

      if (response.success) {
        categories.value.push(response.data)
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Category created successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to create category')
      }
    } catch (error) {
      logService.error('Add Category Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const updateCategory = async (id, categoryData) => {
    try {
      logService.info(`Updating category: ${id}`, categoryData)
      const response = await apiService.put(`/admin/product-categories/${id}`, categoryData)

      if (response.success) {
        const index = categories.value.findIndex(cat => cat.id === id)
        if (index !== -1) {
          categories.value[index] = response.data
        }
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Category updated successfully'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to update category')
      }
    } catch (error) {
      logService.error(`Update Category Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  const deleteCategory = async (id) => {
    try {
      logService.info(`Deleting category: ${id}`)
      const response = await apiService.delete(`/admin/product-categories/${id}`)

      if (response.success) {
        categories.value = categories.value.filter(cat => cat.id !== id)
        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Category deleted successfully'
        })
        return true
      } else {
        throw new Error(response.message || 'Failed to delete category')
      }
    } catch (error) {
      logService.error(`Delete Category Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      throw error
    }
  }

  // ==================== GETTERS ====================

  const getProductById = (id) => {
    return products.value.find(item => item.id === id || item.uid === id)
  }

  const getFeaturedProducts = () => {
    return products.value.filter(item => item.featured)
  }

  const getProductsByCategory = (categoryId) => {
    return products.value.filter(item => item.category_id === categoryId)
  }

  return {
    // State
    products,
    topProducts,
    currentProduct,
    categories,
    loading,
    meta,

    // Product Actions
    fetchProducts,
    fetchProduct,
    fetchAdminProducts,
    addProduct,
    updateProduct,
    deleteProduct,

    // Top Product Actions
    fetchTopProducts,
    toggleTopProduct,
    updateTopOrder,

    // Image Actions
    uploadIcon,
    uploadBanner,
    uploadScreenshot,
    deleteScreenshot,

    // Feature Actions
    addFeature,
    updateFeature,
    deleteFeature,

    // Testimonial Actions
    addTestimonial,
    updateTestimonial,
    deleteTestimonial,

    // Category Actions
    fetchCategories,
    fetchAdminCategories,
    addCategory,
    updateCategory,
    deleteCategory,

    // Getters
    getProductById,
    getFeaturedProducts,
    getProductsByCategory
  }
})
